import React, { useState, useEffect } from 'react';
import useCategorySearch from '../hooks/useCategorySearch';
import useDebounce from '../hooks/useDebounce';

const CategoryAutocomplete = ({ 
    selectedSite, 
    onCategorySelect, 
    placeholder = "카테고리명을 입력하세요 (예: 세럼)" 
    }) => {
    const [inputValue, setInputValue] = useState('');
    const [isHaveInputValue, setIsHaveInputValue] = useState(false);
    const [dropDownItemIndex, setDropDownItemIndex] = useState(-1);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isSelected, setIsSelected] = useState(false); // 카테고리 선택 완료 여부

    // 디바운싱된 검색어 (500ms 지연)
    const debouncedInputValue = useDebounce(inputValue, 500);

    const { 
        categories, 
        error: categoryError, 
        searchCategories, 
        clearResults 
    } = useCategorySearch();

    const changeInputValue = (event) => {
        if (isSelected) return; // 이미 선택된 상태에서는 입력 무시
        const value = event.target.value;
        setInputValue(value);
        setDropDownItemIndex(-1); // 입력 시 선택 인덱스 초기화
        
        // 입력값이 있으면 즉시 드롭다운 표시
        if (value.trim().length > 0) {
        setIsHaveInputValue(true);
        } else {
        setIsHaveInputValue(false);
        }
    };

    const clickDropDownItem = (clickedItem) => {
        setInputValue(clickedItem.name);
        setIsHaveInputValue(false);
        setIsSelected(true); // 카테고리 선택 완료
        onCategorySelect(clickedItem);
        setDropDownItemIndex(-1);
        clearResults(); // 검색 결과 완전히 초기화
    };

    const handleDropDownKey = (event) => {
        // input에 값이 있을때만 작동
        if (isHaveInputValue && Array.isArray(categories) && categories.length > 0) {
        if (
            event.key === 'ArrowDown' &&
            categories.length - 1 > dropDownItemIndex
        ) {
            setDropDownItemIndex(dropDownItemIndex + 1);
        }

        if (event.key === 'ArrowUp' && dropDownItemIndex >= 0) {
            setDropDownItemIndex(dropDownItemIndex - 1);
        }
        
        if (event.key === 'Enter' && dropDownItemIndex >= 0) {
            clickDropDownItem(categories[dropDownItemIndex]);
        }
        }
    };

    useEffect(() => {
        console.log('useEffect called with debouncedInputValue:', debouncedInputValue, 'isInitialized:', isInitialized, 'isSelected:', isSelected);
        
        // 초기 렌더링에서는 API 호출하지 않음
        if (!isInitialized) {
        setIsInitialized(true);
        return;
        }
        
        // 카테고리가 선택된 상태에서는 API 호출하지 않음
        if (isSelected) {
        return;
        }
        
        if (debouncedInputValue === '') {
        setIsHaveInputValue(false);
        clearResults();
        } else {
        setIsHaveInputValue(true);
        const siteMapping = {
            '올리브영': 'oliveyoung',
            '무신사': 'musinsa',
            '위버스': 'weverse',
            '다이소': 'daiso',
            '국립박물관' : 'museum'
        };
        const siteCode = siteMapping[selectedSite] || selectedSite.toLowerCase();
        console.log('Calling searchCategories with:', siteCode, debouncedInputValue);
        searchCategories(siteCode, debouncedInputValue);
        }
    }, [debouncedInputValue, selectedSite, searchCategories, clearResults, isInitialized, isSelected]);

    return (
        <div className="relative">
        {console.log('Rendering CategoryAutocomplete with categories:', categories, 'isArray:', Array.isArray(categories))}
        <div className={`flex flex-row p-4 border border-gray-300 ${
            isHaveInputValue ? 'rounded-t-lg' : 'rounded-lg'
        } focus-within:shadow-lg ${isSelected ? 'bg-gray-100' : ''}`}>
            <input
            type="text"
            value={inputValue}
            onChange={changeInputValue}
            onKeyUp={handleDropDownKey}
            placeholder={placeholder}
            className={`flex-1 m-0 p-0 bg-transparent border-none outline-none text-base ${isSelected ? 'cursor-default' : ''}`}
            readOnly={isSelected}
            />
            {inputValue && (
            <button 
                onClick={() => {
                    setInputValue('');
                    setIsSelected(false);
                    clearResults();
                }}
                className="cursor-pointer text-gray-500 hover:text-gray-700"
            >
                ×
            </button>
            )}
        </div>
        
        {isHaveInputValue && !isSelected && (
            <div className={`block m-0 p-2 bg-white border border-gray-300 ${
            Array.isArray(categories) && categories.length > 0 ? 'border-t-0 rounded-b-lg' : 'rounded-lg'
            } shadow-lg list-none z-10`}>
            {Array.isArray(categories) && categories.length === 0 && !categoryError && (
                <div className="px-4 py-2 text-gray-500">
                해당하는 카테고리가 없습니다
                </div>
            )}
            
            {categoryError && (
                <div className="px-4 py-2 text-red-600">
                {categoryError}
                </div>
            )}
            
            {Array.isArray(categories) && categories.map((category, index) => (
                <div
                key={category.category_id || index}
                onClick={() => clickDropDownItem(category)}
                onMouseOver={() => setDropDownItemIndex(index)}
                className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                    dropDownItemIndex === index ? 'bg-gray-200' : ''
                }`}
                >
                <div className="font-medium text-gray-800">
                    {category.name}
                </div>
                {category.name_en && (
                    <div className="text-xs text-gray-500 mt-1">
                    {category.name_en}
                    </div>
                )}
                </div>
            ))}
            </div>
        )}
        </div>
    );
};

export default CategoryAutocomplete;
